import {Modal, Box, Text, Group, Kbd, Title, Flex, TextInput} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  TABLE_SHORTCUT_DESCRIPTIONS,
  EXPERIMENTAL_TABLE_SHORTCUT_DESCRIPTIONS,
  NOTEBOOK_SHORTCUTS,
  FORMATTING_SHORTCUTS,
  WORKSPACE_SHORTCUTS,
} from 'components/KeyboardShortcutDescriptions';
import * as keyboard from 'util/Keyboard';

export const KEYBOARD_SHORTCUTS_MODAL_KEY = 'KEYBOARD_SHORTCUTS_MODAL';

interface KeyboardShortcutsModalProps {
  opened: boolean;
  onRequestClose: () => void;
}

function KeyboardShortcutsModalHeader({
  searchQuery,
  onSearchChange,
}: {
  searchQuery: string;
  onSearchChange: (value: string) => void;
}): JSX.Element {
  const handleEscapeKey = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      if (!searchQuery) {
        return;
      }
      e.stopPropagation();
      onSearchChange('');
    }
  };

  return (
    <Box p="sm">
      <Title order={4} mb="sm">
        Keyboard Shortcuts
      </Title>
      <Text fz="lg" color="dimmed" fw="normal" mb="sm">
        Browse all available keyboard shortcuts
      </Text>
      <Box onKeyDown={handleEscapeKey} pt="xs">
        <TextInput
          size="lg"
          placeholder="Search shortcuts..."
          value={searchQuery}
          onChange={(e) => {
            onSearchChange(e.currentTarget.value);
          }}
        />
      </Box>
    </Box>
  );
}

function ShortcutKeysGroup({
  normalizedShortcutText,
  skipOsTranslation,
}: {
  normalizedShortcutText: string;
  skipOsTranslation?: boolean;
}): JSX.Element {
  const displayShortcutText = skipOsTranslation
    ? normalizedShortcutText
    : keyboard.getOsSpecificShortcutText(normalizedShortcutText);
  const tokens = displayShortcutText.split(' + ');

  return (
    <Group spacing={4} noWrap={true}>
      {tokens.map((token, tokenIndex) => (
        <React.Fragment key={`${token}-${tokenIndex}`}>
          {tokenIndex > 0 && (
            <Text ml="xs" mr="xs" fz="xl" color="dimmed">
              +
            </Text>
          )}
          <Kbd size="xl" fz="xl" fw="normal" py="sm" px="md">
            {token}
          </Kbd>
        </React.Fragment>
      ))}
    </Group>
  );
}

function KeyboardShortcutRow({shortcut}: {shortcut: keyboard.ShortcutDescription}): JSX.Element {
  const allShortcuts = [shortcut.shortcut, ...(shortcut.alternativeShortcuts || [])];
  return (
    <Box component="li" py="sm" px="md" mb="sm" style={{listStyleType: 'none'}}>
      <Flex justify="space-between" align="center" gap="48px">
        <Text fz="xl" sx={{alignSelf: 'flex-start'}}>
          {shortcut.description}
        </Text>
        <Flex
          direction="column"
          gap="xs"
          align="flex-end"
          sx={{
            '& > *:not(:first-of-type)': {
              marginTop: '8px',
            },
          }}
        >
          {allShortcuts.map((shortcutItem, shortcutIndex) => (
            <Flex key={shortcutIndex} direction="row" gap="md" align="center">
              {shortcutIndex > 0 && (
                <Text fz="xl" color="dimmed">
                  or
                </Text>
              )}
              <ShortcutKeysGroup
                normalizedShortcutText={shortcutItem.normalizedShortcutText}
                skipOsTranslation={shortcutItem.skipOsTranslation}
              />
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Box>
  );
}

function KeyboardShortcutsModalSection({
  section,
  name,
  sectionId,
}: {
  section: Array<keyboard.ShortcutDescription>;
  name: string;
  sectionId: string;
}): JSX.Element {
  return (
    <Box component="section" aria-labelledby={sectionId}>
      <Title id={sectionId} order={4} mb="lg">
        {name}
      </Title>
      <Box component="ul" p="0" aria-label={`${name} shortcuts`}>
        {section.map((shortcut, index) => (
          <KeyboardShortcutRow key={index} shortcut={shortcut} />
        ))}
      </Box>
    </Box>
  );
}

function filterShortcuts(
  shortcuts: Array<keyboard.ShortcutDescription>,
  query: string
): Array<keyboard.ShortcutDescription> {
  if (!query?.trim()) {
    return shortcuts;
  }
  const lowerQuery = query.toLowerCase();
  return shortcuts.filter((shortcut) => shortcut.description.toLowerCase().includes(lowerQuery));
}

function KeyboardShortcutsModal(props: KeyboardShortcutsModalProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 250);

  const notebookShortcuts = [...NOTEBOOK_SHORTCUTS, ...FORMATTING_SHORTCUTS, ...WORKSPACE_SHORTCUTS];
  const tableShortcuts = BenchlingConfig.ELN_ENABLE_TABLE_EXPERIMENTAL_HOTKEYS
    ? [...TABLE_SHORTCUT_DESCRIPTIONS, ...EXPERIMENTAL_TABLE_SHORTCUT_DESCRIPTIONS]
    : TABLE_SHORTCUT_DESCRIPTIONS;

  const filteredNotebookShortcuts = filterShortcuts(notebookShortcuts, debouncedSearchQuery);
  const filteredTableShortcuts = filterShortcuts(tableShortcuts, debouncedSearchQuery);

  return (
    <Modal
      size="sm"
      opened={props.opened}
      onClose={props.onRequestClose}
      closeOnClickOutside={true}
      closeOnEscape={true}
      closeButtonProps={{size: 'xs', style: {alignSelf: 'flex-start'}}}
      radius="lg"
      title={<KeyboardShortcutsModalHeader searchQuery={searchQuery} onSearchChange={setSearchQuery} />}
      styles={{
        title: {width: '100%'},
      }}
      zIndex={1040}
    >
      <Box
        p="sm"
        sx={{
          '& > *:not(:first-of-type)': {
            marginTop: '36px',
          },
        }}
      >
        {filteredNotebookShortcuts.length > 0 && (
          <KeyboardShortcutsModalSection
            section={filteredNotebookShortcuts}
            name="Notebook"
            sectionId="notebook"
          />
        )}
        {filteredTableShortcuts.length > 0 && (
          <KeyboardShortcutsModalSection section={filteredTableShortcuts} name="Table" sectionId="table" />
        )}
        {filteredNotebookShortcuts.length === 0 && filteredTableShortcuts.length === 0 && (
          <Text fz="lg" color="dimmed" ta="center" py="xl">
            No shortcuts found matching "{debouncedSearchQuery}"
          </Text>
        )}
      </Box>
    </Modal>
  );
}

export default KeyboardShortcutsModal;
